import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Price } from "@/lib/currency";
import { waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";

const fmtDate = (d) => d.toISOString().slice(0, 10);
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function ConsultationsPage() {
  const [config, setConfig] = useState(null);
  const [provider, setProvider] = useState("team");
  const [duration, setDuration] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const days = useMemo(() => {
    const list = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      list.push(d);
    }
    return list;
  }, []);

  useEffect(() => {
    api.get("/consultations/config").then((r) => setConfig(r.data)).catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    if (!date || !duration) return;
    setSlotsLoading(true);
    setSlot(null);
    api
      .get(`/consultations/availability?date=${date}&duration=${duration}`)
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [date, duration]);

  const providers = config?.providers || [];
  const selected = providers.find((p) => p.id === provider)?.durations.find((d) => d.duration === duration);

  const pay = async (e) => {
    e.preventDefault();
    if (!slot) return;
    setBusy(true);
    try {
      const res = await api.post("/consultations/book", {
        date,
        start_minutes: slot.start_minutes,
        duration,
        provider,
        name,
        email,
      });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      const msg = formatApiError(err);
      if (msg.toLowerCase().includes("being configured")) {
        toast.info("Online payment opens shortly — message us on WhatsApp to book this slot now.", { duration: 6000 });
      } else {
        toast.error(msg);
      }
      // refresh availability in case the slot was taken
      setDate((d) => d && `${d}`); // no-op state touch
      if (date && duration) {
        const r = await api.get(`/consultations/availability?date=${date}&duration=${duration}`);
        setSlots(r.data.slots);
        setSlot(null);
      }
      setBusy(false);
    }
  };

  return (
    <div className="bg-jawa-ivory" data-testid="consultations-page">
      <Nav />
      <Seo
        title="1-2-1 Consultations — Talk to JÀWÀ | JÀWÀ Africa"
        description="Book a private video consultation with the JÀWÀ team — relocation, investment, travel and settling-in guidance. 15–60 minutes, West Africa Time."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <SectionHeading overline="1-2-1 Consultations" title="Talk it through with someone who knows." />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-jawa-espresso/70">
            A private video call with the JÀWÀ team — relocation questions, investment sense-checks, trip
            planning or settling-in support. Choose a duration, pick a time that suits you, and pay securely
            online. All appointment times are West Africa Time (WAT, UTC+1).
          </p>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-jawa-espresso/55">
            Research, bespoke itinerary production and supplier coordination are separate pieces of work —
            scoped and quoted before you commit, never assumed to be included.
          </p>
        </FadeUp>

        <FadeUp className="mt-12">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-jawa-coral">1 · Choose who you speak with</p>
          <div className="grid gap-4 sm:grid-cols-2" data-testid="provider-grid">
            {providers.map((p) => (
              <button
                key={p.id}
                onClick={() => { setProvider(p.id); setDuration(null); setSlot(null); }}
                data-testid={`provider-${p.id}`}
                className={`border p-6 text-left transition-colors ${
                  provider === p.id
                    ? "border-jawa-espresso bg-jawa-espresso text-jawa-ivory"
                    : "border-jawa-espresso/15 bg-white text-jawa-espresso hover:border-jawa-turquoise"
                }`}
              >
                <p className="font-serif text-xl font-medium">{p.label}</p>
                <p className={`mt-1 text-xs leading-relaxed ${provider === p.id ? "text-jawa-ivory/60" : "text-jawa-espresso/55"}`}>
                  {p.id === "founder"
                    ? "A private session with Elizabeth — founder of JÀWÀ, who made the move herself and has personally hosted 70+ guests home."
                    : "A JÀWÀ specialist on relocation, investment, settling in or planning your trip."}
                </p>
                <p className={`mt-2 text-[11px] font-semibold uppercase tracking-[0.2em] ${provider === p.id ? "text-jawa-gold" : "text-jawa-coral"}`}>
                  From <Price amount={p.durations[0]?.usd} from="USD" />
                </p>
              </button>
            ))}
          </div>
        </FadeUp>

        <FadeUp className="mt-10">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-jawa-coral">2 · Choose your duration</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="duration-grid">
            {(providers.find((p) => p.id === provider)?.durations || []).map((d) => (
              <button
                key={d.duration}
                onClick={() => setDuration(d.duration)}
                data-testid={`duration-${d.duration}`}
                className={`border p-6 text-left transition-colors ${
                  duration === d.duration
                    ? "border-jawa-coral bg-jawa-espresso text-jawa-ivory"
                    : "border-jawa-espresso/15 bg-white hover:border-jawa-turquoise"
                }`}
              >
                <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] opacity-70">
                  <Clock className="h-3.5 w-3.5" /> {d.duration} minutes
                </p>
                <p className="mt-3 font-serif text-3xl font-medium">
                  <Price amount={d.usd} from="USD" />
                </p>
                <p className={`mt-1 text-[11px] ${duration === d.duration ? "text-jawa-ivory/60" : "text-jawa-espresso/50"}`}>
                  ≈ ₦{d.indicative.ngn.toLocaleString()} charged via Paystack
                </p>
              </button>
            ))}
          </div>
        </FadeUp>

        {duration && (
          <FadeUp className="mt-12">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-jawa-coral">3 · Choose your day</p>
            <div className="flex gap-2 overflow-x-auto pb-2" data-testid="date-strip">
              {days.map((d) => {
                const ds = fmtDate(d);
                return (
                  <button
                    key={ds}
                    onClick={() => setDate(ds)}
                    data-testid={`date-${ds}`}
                    className={`shrink-0 border px-4 py-3 text-center transition-colors ${
                      date === ds
                        ? "border-jawa-espresso bg-jawa-espresso text-jawa-ivory"
                        : "border-jawa-espresso/15 bg-white hover:border-jawa-turquoise"
                    }`}
                  >
                    <span className="block text-[10px] uppercase tracking-[0.2em] opacity-60">{DAY_LABELS[d.getDay()]}</span>
                    <span className="block font-serif text-lg font-medium">{d.getDate()} {d.toLocaleString("en-GB", { month: "short" })}</span>
                  </button>
                );
              })}
            </div>

            {date && (
              <div className="mt-8">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-jawa-coral">
                  4 · Choose your time — WAT (UTC+1)
                </p>
                {slotsLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-jawa-turquoise" />
                ) : (
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6" data-testid="slot-grid">
                    {(slots || []).map((s) =>
                      s.available ? (
                        <button
                          key={s.start}
                          onClick={() => setSlot(s)}
                          data-testid={`slot-${s.start}`}
                          className={`border px-2 py-3 text-sm transition-colors ${
                            slot?.start === s.start
                              ? "border-jawa-coral bg-jawa-coral text-white"
                              : "border-jawa-espresso/15 bg-white text-jawa-espresso hover:border-jawa-turquoise"
                          }`}
                        >
                          {s.start}
                        </button>
                      ) : (
                        <span
                          key={s.start}
                          data-testid={`slot-booked-${s.start}`}
                          className="cursor-not-allowed border border-jawa-espresso/10 bg-jawa-espresso/[0.04] px-2 py-3 text-center text-[10px] uppercase tracking-wider text-jawa-espresso/35"
                        >
                          Fully booked
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            )}

            {slot && (
              <form onSubmit={pay} className="mt-10 max-w-lg border border-jawa-espresso/15 bg-white p-8" data-testid="booking-form">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-jawa-coral">4 · Your details</p>
                <div className="mt-4 space-y-3">
                  <input
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    data-testid="booking-name"
                    className="w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm outline-none focus:border-jawa-turquoise"
                  />
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    data-testid="booking-email"
                    className="w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm outline-none focus:border-jawa-turquoise"
                  />
                </div>
                <div className="mt-5 border-t border-jawa-stone pt-4 text-sm text-jawa-espresso/70" data-testid="booking-summary">
                  {provider === "founder" ? "Elizabeth (founder)" : "JÀWÀ team"} · {duration}-minute consultation · {date} · {slot.start} WAT · <Price amount={selected?.usd} from="USD" />{" "}
                  <span className="text-xs text-jawa-espresso/50">(charged in NGN ≈ ₦{selected?.indicative.ngn.toLocaleString()} via Paystack)</span>
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="booking-pay-button"
                  className="mt-6 flex w-full items-center justify-center gap-2 bg-jawa-espresso px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors hover:bg-jawa-coral disabled:opacity-60"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Pay & book securely <ArrowRight className="h-4 w-4" />
                </button>
                <a
                  href={waLink(`Hello JÀWÀ — I'd like to book a ${duration}-minute consultation on ${date} at ${slot.start} WAT.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-whatsapp-alt"
                  className="mt-4 block text-center text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise transition-colors hover:text-jawa-espresso"
                >
                  Prefer to arrange it on WhatsApp?
                </a>
              </form>
            )}
          </FadeUp>
        )}

        <FadeUp className="mt-16">
          <div className="max-w-2xl border border-jawa-espresso/10 bg-white p-8" data-testid="consultation-aftercare">
            <h2 className="flex items-center gap-2 font-serif text-2xl font-medium text-jawa-espresso">
              <CalendarDays className="h-5 w-5 text-jawa-gold" /> What happens after you pay
            </h2>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-jawa-espresso/70">
              <li>Your payment is verified and your slot is confirmed instantly — no one else can book it.</li>
              <li>You receive a branded confirmation with your booking reference, date, time (WAT) and duration.</li>
              <li>Meeting details arrive by email before your appointment.</li>
              <li>Need to rearrange? Reply to the confirmation or WhatsApp us.</li>
            </ul>
          </div>
        </FadeUp>
      </main>
      <Footer />
    </div>
  );
}
